/** PostHog-facing analytics with a no-op sink when no key is configured. */

export type AnalyticsProps = Record<string, string | number | boolean | null | undefined>;

export type AnalyticsSink = {
  capture: (event: string, properties?: AnalyticsProps) => void;
};

const FORBIDDEN_PROP_KEYS = new Set([
  'title',
  'year',
  'wikidata',
  'wikipedia',
  'country',
  'flag',
  'event_title',
  'event_year',
]);

export function assertNoSpoilers(properties: AnalyticsProps = {}): void {
  for (const key of Object.keys(properties)) {
    if (FORBIDDEN_PROP_KEYS.has(key) || /title|year|wikidata|wikipedia|country|flag/i.test(key)) {
      throw new Error(`analytics property "${key}" is not allowed (spoiler risk)`);
    }
  }
}

export function createNoopAnalytics(): AnalyticsSink {
  return { capture() {} };
}

export function createAnalytics(options: {
  key?: string | null;
  sink?: AnalyticsSink;
  capture?: AnalyticsSink['capture'];
}): AnalyticsSink {
  if (options.sink) return wrapSink(options.sink);
  if (options.capture) return wrapSink({ capture: options.capture });
  if (!options.key) return createNoopAnalytics();
  return wrapSink({
    capture(event, properties) {
      const ph = (globalThis as { posthog?: { capture: AnalyticsSink['capture'] } }).posthog;
      if (ph && typeof ph.capture === 'function') ph.capture(event, properties);
    },
  });
}

function wrapSink(sink: AnalyticsSink): AnalyticsSink {
  return {
    capture(event, properties = {}) {
      assertNoSpoilers(properties);
      sink.capture(event, properties);
    },
  };
}

export const PLAY_EVENTS = {
  playOpen: 'play_open',
  roundStart: 'round_start',
  guessSubmit: 'guess_submit',
  roundEnd: 'round_end',
  resultShare: 'result_share',
} as const;
